import { Box, Button, Checkbox, useDisclosure, VStack } from "@chakra-ui/react";
import { Card, FormRow, Select, type SelectRef } from "@components/ui-kit";
import { Trans, t } from "@lingui/macro";
import type { Contact } from "@src/__generated__/graphql";
import ModalBillingEntity from "@src/components/modules/billing-entity/ModalBillingEntity/ModalBillingEntity";
import { LargeSelect } from "@src/components/ui-kit/Select/LargeSelect";
import {
  AddTitle,
  ModalContactPerson,
} from "@src/components/widgets/Modals/ModalContactPerson";
import { can } from "@src/utils/components/permissions";
import { fieldToSelectProps } from "@src/utils/forms/inputHelpers";
import { useStore } from "@src/utils/hooks";
import { BankAccountSelect } from "@src/widgets/BankAccountSelect";
import { CompanySelect } from "@src/widgets/CompanySelect";
import { UserSelect } from "@src/widgets/UserSelect";
import { observer } from "mobx-react-lite";
import React, { useRef } from "react";
import { InvoiceTypeEnum, useInvoiceStore } from "../../../store";

export const ProjectDetailsCard = observer(function ProjectDetailsCard() {
  const { workspaceStore } = useStore();
  const store = useInvoiceStore();
  const billingSelectRef = useRef<SelectRef>(null);
  const client = store.selectedBrands?.[0]?.client;

  const contactModal = useDisclosure();
  const billingModal = useDisclosure();

  const onCreateContact = (contact: Pick<Contact, "id">) => {
    store.form.contact_id.onChange(contact.id);
    store.selectedBrandsRefetch?.();
  };

  const onSelectBrands = (values: string | string[]) => {
    if (!Array.isArray(values)) values = values ? [values] : [];
    store.form.brand_ids.onChange(values);
    store.setClient(values);
    store.resetOnSelectBrands();
    store.fetchExchangeRates();
    store.setOrdersBrandFilter(values);
  };

  const onSelectProjects = (values: string | string[]) => {
    if (!Array.isArray(values)) values = values ? [values] : [];
    store.form.project_ids.onChange(values);
    store.resetOnSelectProjects();
    store.setTeamFromProjects();
    store.fetchProjectsData();
    store.setOrdersProjectFilter(values);
  };

  const onSelectTeam = (value: string | string[]) => {
    const newTeamIds = Array.isArray(value) ? value : [value];

    store.form.team_ids.onChange(newTeamIds);
  };

  return (
    <Box mb="4">
      <Card sectioned>
        <h2 className="text-3xl font-semibold mb-8">
          <Trans>Project Details</Trans>
        </h2>
        <div>
          {store.type === InvoiceTypeEnum.CreditNote && (
            <FormRow title={t`Original invoice`}>
              <LargeSelect
                options={store.originalInvoiceOption ?? []}
                value={store.form.credit_note_outgoing_invoice_id.value}
                error={store.form.credit_note_outgoing_invoice_id.error}
                onChange={store.handleSelectOutgoingInvoice}
                loadOptions={store.fetchOutgoingInvoices}
              />
            </FormRow>
          )}

          <FormRow
            title={
              store.isBrandMultiSelect ? (
                <Trans>Brand/s</Trans>
              ) : (
                <Trans>Brand</Trans>
              )
            }
          >
            <Select
              id="create-rfi-brand-select"
              placeholder={
                store.isBrandMultiSelect ? t`Select brand/s` : t`Select brand`
              }
              isMulti={store.isBrandMultiSelect}
              isDisabled={!store.canEditBrand}
              value={store.form.brand_ids.value}
              options={store.brandsOptions}
              error={store.form.brand_ids.error}
              onChange={onSelectBrands}
            />
          </FormRow>

          {!!store.form.brand_ids.value.length && (
            <React.Fragment>
              <FormRow title={t`Billing Address`}>
                <VStack align="end">
                  <Select
                    id="create-rfi-billing-address-select"
                    placeholder={t`Select billing address`}
                    isDisabled={!store.canEditProfileDetails}
                    value={store.form.billing_entity_id.value}
                    options={store.billingEntitiesOptions}
                    error={store.form.billing_entity_id.error}
                    ref={billingSelectRef}
                    onChange={store.handleSelectBillingEntity}
                    topExtraContent={
                      <Button
                        p="3"
                        onClick={() => {
                          billingSelectRef.current?.onMenuClose();
                          billingModal.onOpen();
                        }}
                        variant="link"
                      >
                        <Trans>+ Add new billing address</Trans>
                      </Button>
                    }
                  />

                  {store.isEdit && !store.isRFI && (
                    <Checkbox
                      isChecked={store.form.refresh_billing_entity.value}
                      onChange={(e) =>
                        store.form.refresh_billing_entity.onChange(
                          e.target.checked,
                        )
                      }
                    >
                      <Trans>Reset billing address</Trans>
                    </Checkbox>
                  )}
                </VStack>
              </FormRow>

              <FormRow
                title={
                  store.isProjectMultiSelect ? (
                    <Trans>Project/s</Trans>
                  ) : (
                    <Trans>Project</Trans>
                  )
                }
              >
                <Select
                  id="create-rfi-projecs-select"
                  placeholder={
                    store.isProjectMultiSelect
                      ? t`Select project/s`
                      : t`Select project`
                  }
                  isDisabled={!store.canEditProfileDetails}
                  isSearchable
                  isMulti={store.isProjectMultiSelect}
                  value={store.form.project_ids.value}
                  options={store.filteredProjectOptions}
                  error={store.form.project_ids.error}
                  onChange={onSelectProjects}
                />
              </FormRow>

              <FormRow title={t`Client contact person`} mb="0">
                <UserSelect
                  id={"create-rfi-contact-person-select"}
                  placeholder={t`Select client contact person`}
                  isDisabled={!store.canEditProfileDetails}
                  isSearchable
                  asPortal
                  topExtraContent={
                    <Button
                      p="3"
                      onClick={() => {
                        contactModal.onOpen();
                      }}
                      variant="link"
                    >
                      <Trans>+ Add new contact person</Trans>
                    </Button>
                  }
                  {...fieldToSelectProps(
                    store.form.contact_id,
                    store.contactsOptions,
                  )}
                />
              </FormRow>
            </React.Fragment>
          )}
        </div>
        <ModalContactPerson
          {...contactModal}
          title={<AddTitle />}
          brandId={
            store.form.brand_ids.value.length === 1
              ? store.form.brand_ids.value[0]
              : undefined
          }
          brandsOptions={store.selectedBrandsOptions}
          onSubmit={onCreateContact}
        />
        {client && (
          <ModalBillingEntity
            {...billingModal}
            client={store.selectedBrands?.[0]?.client}
            onSubmit={async (payload) => {
              billingModal.onClose();
              await store.fetchBillingEntities(store.form.client_id.value);
              store.handleSelectBillingEntity(payload.id);
            }}
          />
        )}
      </Card>

      <Card sectioned my="4">
        {can(
          "requestForInvoicing_create_all",
          "requestForInvoicing_update_all",
          "requestForInvoicing_create_team",
          "requestForInvoicing_update_team",
        ) && (
          <FormRow title={t`Main project manager`}>
            <UserSelect
              id="create-rfi-owner-select"
              isDisabled={!store.canEditProfileDetails}
              placeholder={t`Select owner`}
              {...fieldToSelectProps(store.form.owner_id, store.ownersOptions)}
            />
          </FormRow>
        )}
        <FormRow title={<Trans>Team</Trans>} mb="0">
          <Select
            isDisabled={!store.isFinancier}
            placeholder={t`Select Team`}
            value={store.form.team_ids.value}
            options={store.teamOptions}
            onChange={onSelectTeam}
            error={store.form.team_ids.error}
            isMulti={workspaceStore.settings?.invoicing_more_teams}
          />
        </FormRow>
      </Card>

      <Card sectioned>
        <FormRow
          title={t`Legal entity`}
          display={
            workspaceStore.hasMultipleCompanies(
              store.form.workspace_company_id.value,
            ) || store.form.workspace_company_id.hasError
              ? "flex"
              : "none"
          }
        >
          <CompanySelect
            field={store.form.workspace_company_id}
            onChange={store.handleChangeCompany}
          />
        </FormRow>

        <FormRow
          title={t`Agency bank account`}
          display={store.showBankAccountSelect ? "flex" : "none"}
        >
          <BankAccountSelect
            field={store.form.workspace_bank_account_id}
            companyId={store.form.workspace_company_id.$}
          />
        </FormRow>
      </Card>
    </Box>
  );
});
